import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { Form, Segment, Comment, Button, Header } from "semantic-ui-react";
import "./InspectionItemCommentCard.scss";
import identity from "../../../../selectors/identity";
import {
  InspectionItemCommentDeleteModal,
  IInspectionCommentDeleteItemPreview,
} from "./InspectionItemCommentDeleteModal";
import InspectionItemCommentTooltip from "./InspectionItemCommentTooltip";
import { coreUpload, FileUsage } from "../../../../actions/coreUpload";
import { useMedia } from "react-media";
import { CameraInput } from "../../../../components/CameraInput";
import { ImageCarousel, ImageCarouselSize } from "../../../../components/ImageCarousel";
import { FilePreview } from "../../../../components/FilePreview";
import { MINIMUM_CHUNK_SIZE, multipartUpload, UPLOAD_FAILED } from "../../../../actions/multipartUpload";
import { useToasts } from "react-toast-notifications";
import { ZonedDateTime } from "../../../../components/global_components/ZonedDateTime";
import useCreateInspectionCommentMutation from "../../hooks/inspections/useCreateInspectionCommentMutation";
import useInspectionItemComments from "../../hooks/inspections/useInspectionItemComments";
import useInspectionItemPreviousComments from "../../hooks/inspections/useInspectionItemPreviousComments";
import useDeleteInspectionCommentMutation from "../../hooks/inspections/useDeleteInspectionCommentMutation";
import useUpdateInspectionCommentMutation from "../../hooks/inspections/useUpdateInspectionCommentMutation";
import { ToastStatuses } from "../../../../components/global_components/Toast";

interface IInspectionItemCommentCardProps {
  inspectionId: number;
  sectionId: number;
  itemId: number;
  isStaff: boolean;
  isInspectionComplete: boolean;
  isPublicView: boolean;
}

export type AttachmentFile = {
  fileId: string;
  url: string;
  fileType: string;
  thumbnails?: {
    [K in ImageCarouselSize]: string | null;
  };
};

export const InspectionItemCommentCard = ({
  inspectionId,
  sectionId,
  itemId,
  isStaff,
  isInspectionComplete,
  isPublicView = false,
}: IInspectionItemCommentCardProps) => {
  const dispatch = useDispatch();
  const { addToast } = useToasts();
  const editorRef: any = useRef();
  const editEditorRef: any = useRef();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteModalPreviewItem, setDeleteModalPreviewItem] = useState<IInspectionCommentDeleteItemPreview | null>(
    null,
  );
  const [attachments, setAttachments] = useState<AttachmentFile[]>([]);
  const [attachmentPreview, setAttachmentPreview] = useState<AttachmentFile>();
  const isSmallScreen = useMedia({ query: "(max-width: 691px)" });

  const isPermitAdmin = identity.isPermitsAdmin();
  const isPermitUser = identity.isPermitsUser();
  const userId = identity.getUserId();
  const userCanSendMessages = isPermitAdmin || isPermitUser;

  const conversationQuery = useInspectionItemComments({
    inspectionId: inspectionId,
    sectionId: sectionId,
    itemId: itemId,
  });
  const comments = conversationQuery.data?.data ?? [];
  const [editingAttachments, setEditingAttachments] = useState<AttachmentFile[]>([]);

  const [comment, setComment] = useState("");
  const [editingComment, setEditingComment] = useState<number | null>(null); // Id of comment being edited
  const [editedComment, setEditedComment] = useState<string>("");

  const [attachmentUploading, setAttachmentUploading] = useState(false);

  const { createInspectionItemComment: sendComment, isLoading: sendCommentLoading } =
    useCreateInspectionCommentMutation();

  const { updateComment: editComment, isLoading: editCommentLoading } = useUpdateInspectionCommentMutation(
    inspectionId,
    sectionId,
    itemId,
    {
      onSuccess: () => {
        addToast("Comment updated successfully", { appearance: ToastStatuses.Success, autoDismiss: true });
      },
    },
  );

  const { deleteComment, isLoading: deleteCommentLoading } = useDeleteInspectionCommentMutation(
    inspectionId,
    sectionId,
    itemId,
    {
      onSuccess: () => {
        addToast("Comment deleted successfully", { appearance: ToastStatuses.Success, autoDismiss: true });
      },
    },
  );

  const loadingConversation = conversationQuery.isLoading;

  const onAddComment = () => {
    sendComment(
      inspectionId,
      sectionId,
      itemId,
      comment,
      attachments.map((attachment) => attachment.fileId),
    );
    clearEditor(false);
  };

  const onEditComment = (commentId: number, newComment: string) => {
    editComment(
      commentId,
      newComment,
      editingAttachments.map((attachment) => attachment.fileId),
    );
    clearEditor(true);
  };

  const onDeleteComment = async (commentId?: number) => {
    if (commentId) deleteComment(commentId);
    setIsDeleteModalOpen(false);
  };

  const openDeleteModal = (data: IInspectionCommentDeleteItemPreview) => {
    setDeleteModalPreviewItem(data);
    setIsDeleteModalOpen(true);
  };

  const clearEditor = (isEditing: boolean) => {
    if (isEditing) {
      setEditingComment(null);
      editEditorRef.current?.focusAtStart();
      if (editEditorRef.current) editEditorRef.current.element.children[0].innerText = "";
      setEditingAttachments([]);
      return;
    }
    setComment("");
    editorRef.current?.focusAtStart();
    if (editorRef.current) editorRef.current.element.children[0].innerText = "";
    setAttachments([]);
  };

  const onDeleteAttachment = (index: number, editing: boolean) => {
    const newAttachments = [...(editing ? editingAttachments : attachments)];
    newAttachments.splice(index, 1);
    editing ? setEditingAttachments(newAttachments) : setAttachments(newAttachments);
  };

  const onClickAttachment = (index: number, sourceArray: AttachmentFile[]) => {
    setAttachmentPreview(sourceArray[index]);
  };

  const onFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, isEditing: boolean) => {
    if (!e.target.files) return;
    const files: AttachmentFile[] = isEditing ? editingAttachments : attachments;

    // Triggers the loading attachment display
    const loadingFiles = e?.target.files?.length ?? 0;
    const tempFiles = [...files, ...(Array(loadingFiles).fill({ fileId: "", url: "" }) as AttachmentFile[])];
    isEditing ? setEditingAttachments(tempFiles) : setAttachments(tempFiles);

    const uploads = Array.from(e?.target.files ?? []).map(async (file) => {
      if (file.size > 100 * 1024 * 1024) {
        addToast("File size exceeds the 100MB limit", { appearance: ToastStatuses.Error });
        return;
      }
      setAttachmentUploading(true);
      const uploadOptions = {
        id: `inspection-item-comment-attachment-${file.name}`,
        file,
        usage: FileUsage.InspectionItemCommentAttachments,
      };
      const upload = file.size > MINIMUM_CHUNK_SIZE ? multipartUpload : coreUpload;
      const response = (await dispatch(upload(uploadOptions))) as any;
      if (response.type === UPLOAD_FAILED) {
        addToast("Failed to upload attachment", { appearance: ToastStatuses.Error });
        return;
      }
      files.push({
        fileId: response.payload?.uuid,
        url: response.payload?.url,
        fileType: response.payload?.file.fileExtension,
        thumbnails: response.payload?.thumbnails,
      });
    });
    await Promise.all(uploads);
    isEditing ? setEditingAttachments([...files]) : setAttachments([...files]);
    setAttachmentUploading(false);
  };

  const buildConversationFooter = () => {
    if (!userCanSendMessages) {
      return (
        <div className='footer-read-only-text'>
          Only the application submitter or the listed applicant can send messages
        </div>
      );
    }
    if (isInspectionComplete) {
      return "";
    }
    return (
      <Form reply className='tw-bg-white-dark tw-p-4'>
        <div className='tw-rounded-lg tw-border-2 tw-border-solid tw-border-[#C8CACB] tw-bg-white'>
          <textarea
            placeholder='Add Comment...'
            rows={1}
            className='tw-border-none tw-outline-none'
            value={comment}
            onChange={(ev) => {
              setComment(ev.target.value);
            }}
          />
          {attachments && attachments.length > 0 && (
            <ImageCarousel
              images={attachments}
              size={ImageCarouselSize.Medium}
              onDelete={(index) => onDeleteAttachment(index, false)}
              onClick={(index) => onClickAttachment(index, attachments)}
              isSmallScreen={isSmallScreen}
              editing
            />
          )}
        </div>
        <div className='tw-mt-2 tw-flex tw-justify-between'>
          <div>
            <CameraInput onChange={(e) => onFileUpload(e, false)} isMobile={isSmallScreen} />
          </div>
          <Button
            type='submit'
            className='tw-mr-0'
            onClick={onAddComment}
            loading={sendCommentLoading || attachmentUploading}
            disabled={sendCommentLoading || attachmentUploading || (comment == "" && attachments.length === 0)}
            content='Save'
            aria-label='Send message'
            primary
          />
        </div>
      </Form>
    );
  };

  const { data: previousItemCommentsData } = useInspectionItemPreviousComments({
    inspectionId: inspectionId,
    sectionId: sectionId,
    itemId: itemId,
    options: {
      enabled: !isPublicView,
    },
  });

  const previousItemComments = previousItemCommentsData?.data ?? [];

  if (loadingConversation) return <Segment className='communications-card' />;
  const shouldShowCommentButtons = (user_id: number) =>
    isStaff && !isInspectionComplete && user_id == Number(userId) && !editingComment;

  return (
    <Segment basic className='tw-mt-0 tw-min-h-0 tw-p-0'>
      <Segment basic className='tw-m-0 tw-flex tw-flex-grow tw-flex-col-reverse tw-overflow-y-auto tw-px-4 tw-py-0'>
        <Comment.Group className={"tw-w-full"}>
          {previousItemComments.map((commentData) => {
            const comment = commentData.comment ?? "";
            const firstName = commentData.firstName ?? "";
            const lastName = commentData.lastName ?? "";
            const createdAt = commentData.created_at;
            const updatedAt = commentData.updated_at;

            const attachments: AttachmentFile[] =
              commentData.attachment_ids?.map((attachment) => ({
                ...attachment,
                fileId: attachment.id,
                url: attachment.url ?? "",
              })) ?? [];

            return (
              <InspectionItemComment
                key={commentData.id.toString()}
                comment={comment}
                firstName={firstName}
                lastName={lastName}
                attachments={attachments}
                createdAt={createdAt}
                updatedAt={updatedAt}
                onClickAttachment={(index) => {
                  onClickAttachment(index, attachments);
                }}
              />
            );
          })}
          {comments?.length > 0
            ? comments.map(
                ({ id, comment, created_at, updated_at, firstName, lastName, user_id, attachment_ids }, index) => {
                  const commentAttachments: AttachmentFile[] =
                    attachment_ids?.map((attachment) => ({
                      ...attachment,
                      fileId: attachment.id,
                      url: attachment.url ?? "",
                    })) ?? [];

                  return (
                    <div key={index} className='tw-flex tw-p-0 hover:tw-bg-none'>
                      <div className='tw-w-full tw-pl-0 tw-pt-1'>
                        <div className='tw-flex tw-items-center'>
                          <Header className='tw-mb-0 tw-text-lg tw-font-semibold'>{`${firstName} ${lastName}`}</Header>
                          <div className='tw-mb-1 tw-ml-2 tw-flex tw-text-[#999999]'>
                            <div>
                              <ZonedDateTime value={created_at} formatString='MMM d, yyyy h:mm a' />
                            </div>
                            {updated_at !== created_at && (
                              <InspectionItemCommentTooltip content='&nbsp;edited'>
                                <ZonedDateTime value={updated_at} formatString='MMM d, yyyy h:mm a' />
                              </InspectionItemCommentTooltip>
                            )}
                          </div>
                        </div>
                        {editingComment !== id && (
                          <div className='tw-pt-1'>
                            {comment}

                            {commentAttachments && commentAttachments.length > 0 && (
                              <ImageCarousel
                                images={commentAttachments}
                                size={ImageCarouselSize.Medium}
                                onDelete={(index) => onDeleteAttachment(index, false)}
                                onClick={(index) => onClickAttachment(index, commentAttachments)}
                                isSmallScreen={isSmallScreen}
                              />
                            )}
                          </div>
                        )}
                        {shouldShowCommentButtons(user_id) && (
                          <div>
                            <Button
                              content='Edit'
                              className='tw-mr-6 tw-bg-transparent tw-px-0 hover:tw-bg-[#dcdfec] focus:tw-bg-[#0000001a] focus:tw-ring-2 focus:tw-ring-blue'
                              onClick={() => {
                                if (editCommentLoading) return;
                                setEditingComment(id);
                                setEditedComment(comment ?? "");
                                setEditingAttachments(commentAttachments);
                              }}
                            />
                            <Button
                              content='Delete'
                              className='tw-mr-6 tw-bg-transparent tw-px-0 hover:tw-bg-[#dcdfec] focus:tw-bg-[#0000001a] focus:tw-ring-2 focus:tw-ring-blue'
                              onClick={() => {
                                if (deleteCommentLoading) return;
                                openDeleteModal({ id, firstName, lastName, created_at, comment, commentAttachments });
                              }}
                            />
                          </div>
                        )}
                        {editingComment === id && (
                          <Form>
                            <div className='tw-rounded-lg tw-border-2 tw-border-solid tw-border-[#C8CACB] tw-bg-white'>
                              <textarea
                                className='tw-border-none tw-outline-none'
                                value={editedComment}
                                rows={1}
                                onChange={(ev) => {
                                  setEditedComment(ev.currentTarget.value);
                                }}
                              />
                              {editingAttachments && editingAttachments.length > 0 && (
                                <ImageCarousel
                                  images={editingAttachments}
                                  size={ImageCarouselSize.Small}
                                  onDelete={(index) => onDeleteAttachment(index, true)}
                                  onClick={(index) => onClickAttachment(index, editingAttachments)}
                                  isSmallScreen={isSmallScreen}
                                  editing
                                />
                              )}
                            </div>
                            <div className='tw-my-2 tw-flex tw-justify-between'>
                              <div>
                                <CameraInput onChange={(e) => onFileUpload(e, true)} isMobile={isSmallScreen} />
                              </div>
                              <div>
                                <Button basic content='Cancel' onClick={() => clearEditor(true)} />
                                <Button
                                  type='submit'
                                  onClick={() => {
                                    if (editCommentLoading) return;
                                    onEditComment(id, editedComment);
                                  }}
                                  loading={editCommentLoading || attachmentUploading}
                                  disabled={
                                    editCommentLoading ||
                                    attachmentUploading ||
                                    (editedComment == "" && editingAttachments.length === 0)
                                  }
                                  content='Save'
                                  aria-label='Save edited message'
                                  primary
                                />
                              </div>
                            </div>
                          </Form>
                        )}
                      </div>
                    </div>
                  );
                },
              )
            : isPublicView && <div className='tw-w-full tw-p-4 tw-text-center'>No comments found</div>}
        </Comment.Group>
      </Segment>

      {!isPublicView && (
        <Segment basic className='tw-p-0'>
          {buildConversationFooter()}
        </Segment>
      )}
      {isDeleteModalOpen && (
        <InspectionItemCommentDeleteModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={() => onDeleteComment(deleteModalPreviewItem?.id)}
          previewItem={deleteModalPreviewItem}
        />
      )}
      <FilePreview
        isOpen={!!attachmentPreview}
        onClose={() => setAttachmentPreview(undefined)}
        file={attachmentPreview}
      />
    </Segment>
  );
};

interface InspectionItemCommentProps {
  comment: string;
  attachments: AttachmentFile[];
  onClickAttachment: (index: number) => void;
  firstName: string;
  lastName: string;
  createdAt: string;
  updatedAt: string;
}
function InspectionItemComment({
  comment,
  attachments,
  firstName,
  lastName,
  createdAt,
  updatedAt,
  onClickAttachment,
}: InspectionItemCommentProps) {
  const isSmallScreen = useMedia({ query: "(max-width: 691px)" });
  return (
    <div className='tw-w-full tw-pl-0 tw-pt-1'>
      <div className='tw-flex tw-items-center'>
        <Header className='tw-mb-0 tw-text-lg tw-font-semibold'>{`${firstName} ${lastName}`}</Header>
        <div className='tw-mb-1 tw-ml-2 tw-flex tw-text-[#999999]'>
          <div>
            <ZonedDateTime value={createdAt} formatString='MMM d, yyyy h:mm a' />
          </div>
          {updatedAt !== createdAt && (
            <InspectionItemCommentTooltip content='&nbsp;edited'>
              <ZonedDateTime value={updatedAt} formatString='MMM d, yyyy h:mm a' />
            </InspectionItemCommentTooltip>
          )}
        </div>
      </div>

      <div className='tw-pt-1'>
        {comment}

        {attachments && attachments.length > 0 && (
          <ImageCarousel
            images={attachments}
            size={ImageCarouselSize.Medium}
            onClick={onClickAttachment}
            isSmallScreen={isSmallScreen}
          />
        )}
      </div>
    </div>
  );
}
